/* Серверный источник «Озвучки проектов» для шаблона (engine.ts): черновики, автосохранение с ревизией,
   цена, платные операции (озвучка, прослушивание реплики, переозвучка), звук и отправка в чат.
   Шаблон держит свою модель проекта; сюда приходят только правки в формате API (ScriptProjectEdit). */
import { endpoints } from '../../api';
import { ApiError, uuid } from '../../api/client';
import type { ScriptOperation, ScriptProject, ScriptProjectCard, ScriptProjectEdit, ScriptQuote } from '../../api/scripts';
import type { Me } from '../../api/types';
import { clearDeliveryIntent, deliveryIntent } from '../history/history-delivery-intent';
import { studioApi } from '../studio/studio-api';
import { scriptToolUnit } from './projects-entry';
import { scriptError, scriptValidation } from './script-model';
import { uiText } from '../../i18n';
import {
  readScriptIntents,
  scriptIntentSlot,
  scriptOperationPending,
  writeScriptIntents,
  type ScriptIntent,
  type ScriptIntents,
} from './script-intents';

const SAVE_MS = 1200;
const POLL_MS = 2500;
const PAGE = 25;
/** Отказ, после которого тот же ключ повторять бессмысленно: нужен новый quote */
const DEFINITE = new Set([
  'price_changed', 'script_price_changed', 'insufficient_tokens', 'insufficient_funds', 'voice_not_selected', 'voice_unavailable',
  'version_conflict', 'project_revision_changed', 'line_not_found', 'line_text_empty', 'script_render_active', 'result_version_conflict',
]);

/** Сохранение одного проекта: sid — id на сервере (null — ещё не создан), rev — ревизия, sent — последняя принятая правка */
export interface ScriptDoc {
  sid: number | null;
  rev: number;
  saved: ScriptProject | null;
  sent: string;
  edit: (() => ScriptProjectEdit) | null;
  timer: ReturnType<typeof setTimeout> | 0;
  chain: Promise<void>;
  createKey: string | null;
  conflict: boolean;
  err: string;
  quote: ScriptQuote | null;
  quoteErr: string;
  gone: boolean;
}
export type ScriptChange = 'saved' | 'error' | 'quote';
export interface ScriptCaps { read: boolean; write: boolean; preview: boolean; render: boolean; revoice: boolean }

/** Отказ, после которого прежний ключ снят: показать текст и вернуть человека к форме */
export class ScriptFinal extends Error {}
/** Исход запроса неизвестен: ключ остаётся, повтор с ним не спишет Circle дважды */
export class ScriptUncertain extends Error {}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const json = (edit: ScriptProjectEdit) => JSON.stringify(edit);
const finalStatus = (op: ScriptOperation) => op.status === 'ready' || op.status === 'failed' || op.status === 'blocked';

export function createScriptServer(hooks: { changed(kind: ScriptChange, doc: ScriptDoc): void }) {
  let owner: number | null = null;
  const closedCaps = (): ScriptCaps => ({ read: false, write: false, preview: false, render: false, revoice: false });
  let caps: ScriptCaps = closedCaps();
  let cursor: string | null = null;
  /** Цена за 100 знаков из каталога инструментов (0 — не пришла) и её запрос при входе */
  let unit = 0;
  let unitReady: Promise<number> = Promise.resolve(0);
  const audio = new Map<string, Promise<Blob>>();
  const urls = new Map<string, string>();
  const watching = new Map<string, number>();

  function doc(saved?: ScriptProject): ScriptDoc {
    return {
      sid: saved?.id ?? null, rev: saved?.revision ?? 0, saved: saved ?? null, sent: '', edit: null, timer: 0,
      chain: Promise.resolve(), createKey: null, conflict: false, err: '', quote: null, quoteErr: '', gone: false,
    };
  }

  /** Вход в раздел: владелец, возможности и первая страница черновиков. Смена владельца сбрасывает звук прежнего */
  async function load(): Promise<ScriptProjectCard[]> {
    caps = closedCaps();
    /* цена за 100 знаков — из строки инструмента каталога, вход её не ждёт; каталог не ответил — шапка покажет цену проекта */
    unitReady = studioApi.allTools().then(res => (unit = scriptToolUnit(res.items)), () => unit);
    const [me, page, capsRes] = await Promise.all([
      endpoints.me(),
      endpoints.scripts.list(undefined, PAGE),
      endpoints.scripts.capabilities().catch(() => null),
    ]);
    if (owner !== null && owner !== me.user.id) release();
    owner = me.user.id;
    const s = capsRes?.script;
    if (s) caps = { read: s.read_enabled === true, write: s.write_enabled === true, preview: s.preview_enabled === true, render: s.render_enabled === true, revoice: s.revoice_enabled === true };
    cursor = page.next_cursor;
    return page.items;
  }
  async function more(): Promise<ScriptProjectCard[]> {
    if (!cursor) return [];
    const page = await endpoints.scripts.list(cursor, PAGE);
    cursor = page.next_cursor;
    return page.items;
  }

  /* ── черновик ── */
  /** Правка на экране: сохранение уходит через паузу, последняя правка берётся в момент отправки */
  function touch(d: ScriptDoc, edit: () => ScriptProjectEdit) {
    if (d.gone) return;
    d.edit = edit;
    if (!d.conflict && d.err) d.err = '';
    if (d.timer) clearTimeout(d.timer);
    d.timer = setTimeout(() => { d.timer = 0; void flush(d); }, SAVE_MS);
  }
  /** Сохранить сейчас: запросы одного проекта идут по очереди, создание — с ключом идемпотентности до ответа */
  function flush(d: ScriptDoc): Promise<void> {
    if (d.timer) { clearTimeout(d.timer); d.timer = 0; }
    d.chain = d.chain.then(() => saveNow(d));
    return d.chain;
  }
  async function saveNow(d: ScriptDoc) {
    if (!d.edit || d.gone || d.conflict) return;
    if (!caps.write) { d.err = 'Сохранение проектов сейчас недоступно. Обновите данные.'; hooks.changed('error', d); return; }
    const edit = d.edit();
    if (json(edit) === d.sent) return;
    if (scriptValidation({ name: edit.name ?? '', roles: edit.roles ?? [], lines: edit.lines ?? [] })) return;
    try {
      let value: ScriptProject;
      if (d.sid === null) {
        d.createKey ??= uuid();
        value = await endpoints.scripts.create(edit, d.createKey);
        d.createKey = null;
      } else value = await endpoints.scripts.save(d.sid, d.rev, edit);
      d.saved = value;
      d.sid = value.id;
      d.rev = value.revision;
      d.sent = json(edit);
      d.err = '';
      d.quote = null;
      d.quoteErr = '';
      hooks.changed('saved', d);
    } catch (cause) {
      d.conflict = cause instanceof ApiError && cause.status === 412;
      d.err = scriptError(cause);
      hooks.changed('error', d);
    }
  }
  /** На экране есть правки, которых сервер ещё не принял */
  const dirty = (d: ScriptDoc) => !!d.timer || d.sid === null || (!!d.edit && json(d.edit()) !== d.sent);
  /** Свежая версия с сервера после конфликта: правки этого окна уходят */
  async function reload(d: ScriptDoc): Promise<ScriptProject> {
    if (d.sid === null) throw new Error('script_not_saved');
    const value = await endpoints.scripts.get(d.sid);
    adopt(d, value);
    return value;
  }
  /** Версия сервера становится принятой: то, что на экране, совпадает с ней */
  function adopt(d: ScriptDoc, value: ScriptProject) {
    d.saved = value; d.sid = value.id; d.rev = value.revision; d.conflict = false; d.err = ''; d.quote = null; d.quoteErr = '';
    d.sent = '';
  }
  /** Отметить принятую правку после загрузки проекта: шаблон собирает ту же правку из своей модели */
  function settle(d: ScriptDoc, edit: ScriptProjectEdit) { d.sent = json(edit); }
  async function get(id: number) {
    return endpoints.scripts.get(id);
  }
  /** Удалить: отложенное сохранение снимается, идущее дожидается — удаление идёт с последней ревизией */
  async function remove(d: ScriptDoc) {
    if (!caps.write) throw new ScriptFinal('Изменение проектов сейчас недоступно. Обновите данные.');
    d.gone = true;
    if (d.timer) { clearTimeout(d.timer); d.timer = 0; }
    await d.chain;
    if (d.sid !== null) await endpoints.scripts.remove(d.sid, d.rev);
  }

  /* ── цена ── */
  /** Цена озвучки текущей ревизии; правки ещё не приняты — сначала сохранение */
  async function quote(d: ScriptDoc): Promise<ScriptQuote | null> {
    await flush(d);
    if (d.sid === null || dirty(d) || d.conflict) return null;
    const sid = d.sid, rev = d.rev;
    try {
      const q = await endpoints.scripts.quote(sid, rev);
      if (q.project_id !== sid || q.project_revision !== rev || !(q.amount > 0)) throw new Error('quote_invalid');
      if (d.rev !== rev) return null;
      d.quote = q;
      d.quoteErr = '';
      hooks.changed('quote', d);
      return q;
    } catch (cause) {
      if (d.rev === rev) { d.quote = null; d.quoteErr = scriptError(cause); hooks.changed('quote', d); }
      return null;
    }
  }
  const fresh = (d: ScriptDoc) => (!dirty(d) && d.quote && d.quote.project_revision === d.rev ? d.quote : null);
  async function quoteLine(d: ScriptDoc, lineKey: string): Promise<ScriptQuote> {
    await flush(d);
    if (d.sid === null || dirty(d)) throw new ScriptFinal(d.err || 'Проект ещё сохраняется. Попробуйте через секунду.');
    const q = await endpoints.scripts.quote(d.sid, d.rev, lineKey);
    if (q.project_id !== d.sid || q.project_revision !== d.rev || q.line_key !== lineKey || !(q.amount > 0)) throw new Error('quote_invalid');
    return q;
  }
  async function quoteRevoice(d: ScriptDoc, op: ScriptOperation, lineKey: string): Promise<ScriptQuote> {
    if (d.sid === null || !op.order_id || !op.result_revision) throw new ScriptFinal('Результат больше недоступен.');
    const q = await endpoints.scripts.revoiceQuote(d.sid, op.order_id, lineKey, op.result_revision);
    if (q.project_id !== d.sid || q.result_revision !== op.result_revision || q.line_key !== lineKey || !(q.amount > 0)) throw new Error('quote_invalid');
    return q;
  }

  /* ── операции ── */
  function intents(sid: number): ScriptIntents {
    if (owner === null) throw new ScriptFinal('Не удалось проверить владельца проекта. Обновите страницу.');
    try { return readScriptIntents(owner, sid); }
    catch { throw new ScriptFinal('Не удалось восстановить операции. Не запускайте озвучку повторно; проверьте историю.'); }
  }
  function store(sid: number, slot: string, intent: ScriptIntent | null) {
    if (owner === null) throw new ScriptFinal('Не удалось проверить владельца проекта.');
    const next = { ...intents(sid) };
    if (intent) next[slot] = intent; else delete next[slot];
    writeScriptIntents(owner, sid, next);
  }
  /** Операция слота, которая ещё идёт или исход которой неизвестен */
  function pending(d: ScriptDoc, kind: ScriptIntent['kind'], lineKey?: string): ScriptIntent | null {
    if (d.sid === null || owner === null) return null;
    try {
      const it = readScriptIntents(owner, d.sid)[scriptIntentSlot(kind, lineKey)];
      return it && (it.kind === kind || kind !== 'preview') && (scriptOperationPending(it) || !!it.uncertainRequest) ? it : null;
    } catch { return null; }
  }
  /** Идущая озвучка проекта по серверу (другое окно, перезагрузка): ключ и цена сохраняются, чтобы следить за ней */
  async function discover(d: ScriptDoc): Promise<ScriptIntent | null> {
    const own = pending(d, 'render');
    if (own) return own;
    if (d.sid === null || !d.saved) return null;
    const value = await endpoints.scripts.latest(d.sid);
    const active = value.latest_render;
    if (!active || !scriptOperationPending({ operation: active } as ScriptIntent)) return null;
    const it: ScriptIntent = {
      kind: 'render', key: uuid(), revision: active.project_revision, price: Number(active.amount_tokens) || 1,
      snapshot: d.saved, operation: active,
    };
    store(d.sid, 'render', it);
    return it;
  }

  /** Платная операция: ключ и снимок пишутся до POST; отказ с гарантией снимает ключ, сетевой сбой оставляет его */
  async function submit(d: ScriptDoc, intent: ScriptIntent, onOp: (op: ScriptOperation) => void): Promise<ScriptOperation> {
    const sid = d.sid!;
    const slot = scriptIntentSlot(intent.kind, intent.lineKey);
    store(sid, slot, intent);
    let op: ScriptOperation;
    try {
      op = intent.kind === 'preview'
        ? await endpoints.scripts.preview(sid, intent.revision, intent.lineKey!, intent.price, intent.key)
        : intent.kind === 'render'
          ? await endpoints.scripts.render(sid, intent.revision, intent.price, intent.key)
          : await endpoints.scripts.revoice(sid, intent.orderId!, intent.lineKey!, intent.resultRevision!, intent.price, intent.key);
    } catch (cause) {
      if (cause instanceof ApiError && !intent.uncertainRequest && [402, 409, 412, 422].includes(cause.status) && DEFINITE.has(cause.code)) {
        store(sid, slot, null);
        throw new ScriptFinal(scriptError(cause));
      }
      store(sid, slot, { ...intent, uncertainRequest: true });
      throw new ScriptUncertain(scriptError(cause));
    }
    store(sid, slot, { ...intent, operation: op, uncertainRequest: false });
    onOp(op);
    return watch(sid, slot, onOp);
  }
  /** Следить за операцией до итога; вкладка скрыта — опрос ждёт. Один наблюдатель на слот: новый снимает прежний */
  async function watch(sid: number, slot: string, onOp: (op: ScriptOperation) => void): Promise<ScriptOperation> {
    const id = `${sid}:${slot}`, gen = (watching.get(id) ?? 0) + 1;
    watching.set(id, gen);
    for (;;) {
      const it = intents(sid)[slot];
      if (!it?.operation) throw new ScriptUncertain('Операция не найдена. Проверьте историю.');
      if (finalStatus(it.operation)) { if (watching.get(id) === gen) watching.delete(id); return it.operation; }
      await sleep(Math.max(1000, it.operation.poll_after_ms ?? POLL_MS));
      if (watching.get(id) !== gen) throw new ScriptUncertain('');
      if (document.visibilityState === 'hidden') continue;
      let op: ScriptOperation;
      try { op = await endpoints.scripts.operation(sid, it.operation.operation_id); }
      catch { continue; }
      const now = intents(sid)[slot];
      if (!now || now.key !== it.key) throw new ScriptUncertain('');
      store(sid, slot, { ...now, operation: op });
      onOp(op);
    }
  }
  /** Итог показан: слот освобождается для следующей операции */
  function done(d: ScriptDoc, kind: ScriptIntent['kind'], lineKey?: string) {
    if (d.sid !== null) try { store(d.sid, scriptIntentSlot(kind, lineKey), null); } catch { /* слот перечитается при следующем входе */ }
  }

  function intentOf(d: ScriptDoc, kind: ScriptIntent['kind'], q: ScriptQuote, lineKey?: string, op?: ScriptOperation): ScriptIntent {
    if (d.sid === null || !d.saved) throw new ScriptFinal('Проект ещё не сохранён.');
    return {
      kind, key: uuid(), revision: d.rev, price: q.amount, lineKey,
      orderId: kind === 'revoice' ? op?.order_id : undefined,
      resultRevision: kind === 'revoice' ? op?.result_revision : undefined,
      snapshot: d.saved,
    };
  }
  /** Озвучить проект по показанной цене; идущая или неподтверждённая озвучка продолжается тем же ключом */
  async function render(d: ScriptDoc, onOp: (op: ScriptOperation) => void): Promise<ScriptOperation> {
    if (d.sid === null) throw new ScriptFinal('Проект ещё не сохранён.');
    const was = pending(d, 'render');
    if (was?.operation && !was.uncertainRequest) { onOp(was.operation); return watch(d.sid, 'render', onOp); }
    if (was) return submit(d, was, onOp);
    if (!caps.render) throw new ScriptFinal('Озвучка проектов сейчас недоступна. Обновите данные.');
    const q = fresh(d);
    if (!q) throw new ScriptFinal('Цена изменилась. Проверьте новую цену перед запуском.');
    return submit(d, intentOf(d, 'render', q), onOp);
  }
  /** Следить за озвучкой, найденной при входе */
  function follow(d: ScriptDoc, onOp: (op: ScriptOperation) => void) {
    return watch(d.sid!, 'render', onOp);
  }
  async function preview(d: ScriptDoc, lineKey: string, q: ScriptQuote | null): Promise<ScriptOperation> {
    if (d.sid === null) throw new ScriptFinal('Проект ещё не сохранён.');
    const was = pending(d, 'preview', lineKey);
    const slot = scriptIntentSlot('preview', lineKey);
    if (was?.operation && !was.uncertainRequest) return watch(d.sid, slot, () => {});
    if (was) return submit(d, was, () => {});
    if (!caps.preview) throw new ScriptFinal('Прослушивание реплики сейчас недоступно. Обновите данные.');
    if (!q) throw new ScriptFinal('Цена изменилась. Проверьте новую цену перед запуском.');
    return submit(d, intentOf(d, 'preview', q, lineKey), () => {});
  }
  async function revoice(d: ScriptDoc, op: ScriptOperation, lineKey: string, q: ScriptQuote): Promise<ScriptOperation> {
    if (!caps.revoice) throw new ScriptFinal('Переозвучка реплики сейчас недоступна. Обновите данные.');
    if (pending(d, 'render')) throw new ScriptFinal('Озвучка уже выполняется. Дождитесь результата.');
    return submit(d, intentOf(d, 'revoice', q, lineKey, op), () => {});
  }

  /* ── звук и чат ── */
  const audioKey = (sid: number, op: ScriptOperation) => `${sid}:${op.operation_id}:${op.result_revision ?? 0}`;
  function blob(sid: number, op: ScriptOperation): Promise<Blob> {
    const k = audioKey(sid, op);
    let p = audio.get(k);
    if (!p) {
      p = endpoints.scripts.audio(sid, op.operation_id);
      audio.set(k, p);
      p.catch(() => audio.delete(k));
    }
    return p;
  }
  async function url(sid: number, op: ScriptOperation): Promise<string> {
    const k = audioKey(sid, op);
    const known = urls.get(k);
    if (known) return known;
    const u = URL.createObjectURL(await blob(sid, op));
    urls.set(k, u);
    return u;
  }
  function release() {
    for (const u of urls.values()) URL.revokeObjectURL(u);
    urls.clear();
    audio.clear();
    watching.clear();
  }
  /** Отправить дорожку в чат: владелец сверяется заново, ключ доставки переживает перезагрузку */
  async function send(op: ScriptOperation): Promise<'sent' | 'accepted'> {
    if (!owner || !op.order_id || op.status !== 'ready') throw new ScriptFinal('Дорожка ещё не готова.');
    const me = await endpoints.me();
    if (me.user.id !== owner) throw new ScriptFinal('Аккаунт сменился. Обновите страницу.');
    const intent = deliveryIntent(owner, op.order_id, op.result_revision ?? 1, 'audio');
    const res = await endpoints.history.send(
      { id: op.order_id, kind: 'product', screen: 'script', status: 'done', created_at: op.created_at, result_revision: op.result_revision },
      'audio', intent.key,
    );
    const st = res.delivery?.state;
    if (st === 'sent' || st === 'delivered') { clearDeliveryIntent(intent); return 'sent'; }
    if (['pending', 'queued', 'accepted', 'sending'].includes(st ?? '')) return 'accepted';
    throw new ScriptUncertain('Отправка пока не подтверждена. Нажмите ещё раз, чтобы проверить прежний запрос.');
  }
  /** После платной операции баланс берётся снимком сервера, не считается на экране */
  async function account() {
    try {
      const me: Me = await endpoints.me();
      (window as unknown as { BotServerMe?: Me }).BotServerMe = me;
      document.dispatchEvent(new CustomEvent('steos:account-updated', { detail: me }));
    } catch { /* баланс обновится при следующем входе */ }
  }

  const uncertain = (e: unknown) => e instanceof ScriptUncertain;
  /* причина словами на языке профиля: тексты ScriptFinal и ScriptUncertain — ключи словаря */
  const err = (e: unknown) => (e instanceof ScriptFinal || e instanceof ScriptUncertain ? uiText(e.message) : scriptError(e));
  const check = (edit: ScriptProjectEdit) => scriptValidation({ name: edit.name ?? '', roles: edit.roles ?? [], lines: edit.lines ?? [] });

  return {
    id: uuid, doc, load, more, get cursor() { return cursor; }, get unit() { return unit; }, get unitReady() { return unitReady; }, get caps() { return caps; }, get owner() { return owner; },
    touch, flush, dirty, reload, adopt, settle, get, remove,
    quote, fresh, quoteLine, quoteRevoice,
    pending, discover, render, follow, preview, revoice, done,
    url, blob, send, account, release, err, uncertain, check,
  };
}
export type ScriptServer = ReturnType<typeof createScriptServer>;
